import React, { useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { GridCloseIcon } from "@mui/x-data-grid";
import { useAppContext } from "../../context/AppContext";
import { MembershipPaymentsAPI } from "../../api/membership-payments";

const AddMembershipPaymentModal = ({
  open,
  handleClose,
  setIsLoading,
  memberId,
}) => {
  const { openSnack } = useAppContext();

  const [formState, setFormState] = useState({
    payment_date: dayjs(new Date()),
    amount: "",
    note: "",
  });
  const [formError, setFormError] = useState({});

  const handleError = (formObj = formState) => {
    const temp = { ...formError };

    if ("payment_date" in formObj) {
      temp.payment_date = formObj.payment_date
        ? ""
        : "Payment date is required.";
    }

    if ("amount" in formObj) {
      if (formObj.amount === "" || formObj.amount === null) {
        temp.amount = "Amount is required.";
      } else if (
        Number.isNaN(Number(formObj.amount)) ||
        Number(formObj.amount) <= 0
      ) {
        temp.amount = "Amount must be greater than 0.";
      } else {
        temp.amount = "";
      }
    }

    setFormError({ ...temp });

    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (key, value) => {
    setFormState((prevState) => ({ ...prevState, [key]: value }));
    handleError({ [key]: value });
  };

  const addPayment = async (form) => {
    const formObj = {
      payment_date: dayjs(form?.payment_date)?.format("YYYY-MM-DD"),
      amount: Number(form?.amount),
    };
    if (form?.note && form.note.trim() !== "") {
      formObj.note = form.note.trim();
    }

    try {
      await MembershipPaymentsAPI.addPayment(memberId, formObj);
      openSnack("Payment recorded successfully.", "success");
      setIsLoading(true);
      handleClose();
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  };

  const handleSubmit = () => {
    if (
      handleError({
        payment_date: formState.payment_date,
        amount: formState.amount,
      })
    ) {
      addPayment(formState);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) =>
        reason === "backdropClick" ? "" : handleClose()
      }
      hideBackdrop
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="custom-modal"
      sx={{
        ".MuiPaper-root": {
          width: "640px",
        },
      }}
    >
      <div className="modal-header">
        <h2 className="header-text">Record New Payment</h2>
        <IconButton onClick={() => handleClose()}>
          <GridCloseIcon />
        </IconButton>
      </div>
      <Grid container spacing={2} rowSpacing={2.5} sx={{ padding: "16px 0" }}>
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Payment Date*"
            value={formState?.payment_date || null}
            onChange={(newValue) => handleChange("payment_date", newValue)}
            className="login-textField date-textField"
            sx={{ width: "100%", height: "40px" }}
            format="MM/DD/YYYY"
            slotProps={{
              textField: {
                margin: "normal",
                fullWidth: true,
                id: "payment_date",
                name: "payment_date",
                className: "login-textField",
                size: "small",
                error: !!formError.payment_date,
                helperText: formError.payment_date,
              },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="normal"
            fullWidth
            id="amount"
            label="Amount*"
            name="amount"
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            value={formState?.amount ?? ""}
            onChange={(e) => handleChange("amount", e.target.value)}
            className="login-textField"
            size="small"
            error={!!formError.amount}
            helperText={formError.amount}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            margin="normal"
            fullWidth
            multiline
            minRows={2}
            id="note"
            label="Note"
            name="note"
            value={formState?.note || ""}
            onChange={(e) => handleChange("note", e.target.value)}
            className="login-textField"
            size="small"
          />
        </Grid>
      </Grid>

      <DialogActions sx={{ paddingTop: "8px" }} className="modal-btn-container">
        <Button onClick={handleClose} className="button-common light-blue-bg">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="add-new-button button-common">
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMembershipPaymentModal;
